import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { login as loginRequest, logoutRequest, checkStatus, clearNonce, CurrentUser } from '../lib/store/auth';
import { getCartKey, clearCartKey, addToCart, clearCart, CartResponse } from '../lib/store/cart';
import { cartKeys } from '../hooks/useCart';

interface AuthContextValue {
    isLoggedIn: boolean;
    user: CurrentUser | null;
    isCheckingAuth: boolean;
    authError: string | null;
    login: (username: string, password: string) => Promise<boolean>;
    logout: () => Promise<void>;
    refreshAuthState: (options?: { silent?: boolean }) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ------------------------------------------------------------
// Odczytuje ilość z pozycji koszyka niezależnie od tego, czy CoCart
// zwrócił ją jako liczbę, czy jako obiekt { value: ... } (oba kształty
// występują w praktyce w odpowiedziach CoCart).
// ------------------------------------------------------------
const getQuantityValue = (quantity: any): number => {
    if (typeof quantity === 'number') return quantity;
    if (typeof quantity === 'object' && quantity !== null) {
        return Number(quantity.value ?? quantity.quantity ?? 1);
    }
    return 1;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const queryClient = useQueryClient();
    const isMergingRef = useRef(false);

    const [user, setUser] = useState<CurrentUser | null>(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const [authError, setAuthError] = useState<string | null>(null);

    // ------------------------------------------------------------
    // Koszyk lokalny (gościa) ZASTĘPUJE koszyk konta — świadomie NIE
    // sumujemy go z tym, co może już leżeć na koncie z poprzednich sesji.
    // Powód: koszyk konta z poprzedniej sesji to często nieaktualne dane
    // (stary test, porzucone zakupy sprzed tygodni) — doklejanie do tego
    // świeżych, aktywnych pozycji z bieżącej przeglądarki prowadziło do
    // niekontrolowanego narastania ilości przy każdym kolejnym logowaniu.
    // Jedyne dane, które się liczą, to to, co klient ma w koszyku TERAZ,
    // w tej przeglądarce.
    //
    // Nie polegamy też na automatycznym mechanizmie WooCommerce/CoCart
    // uruchamianym przy "wp_login" — nasz endpoint logowania to zwykłe
    // żądanie REST, nie klasyczny formularz wp-login.php, więc ten
    // automatyzm działał niedeterministycznie. Tutaj całość jest jawnie
    // sterowana przez nasz kod.
    // ------------------------------------------------------------
    const mergeGuestCartIntoAccount = useCallback(async () => {
        if (isMergingRef.current) return;

        const guestCartKey = getCartKey();
        const guestCart = queryClient.getQueryData<CartResponse>(cartKeys.detail());
        const items = guestCart?.items || [];

        // Brak lokalnego koszyka (albo jest pusty) = nie ma czym zastępować,
        // zostawiamy koszyk konta bez zmian.
        if (!guestCartKey || items.length === 0) {
            clearCartKey();
            return;
        }

        isMergingRef.current = true;

        try {
            // Krok 1: wyczyść to, co jest aktualnie na koncie. W tym momencie
            // jesteśmy już uwierzytelnieni (nonce obecny), więc CoCart czyści
            // koszyk KONTA — to jest tu celowe, w przeciwieństwie do próby
            // czyszczenia cudzego cart_key.
            try {
                await clearCart();
            } catch (clearError) {
                console.error('❌ Nie udało się wyczyścić koszyka konta:', clearError);
            }

            // Krok 2: dołóż pozycje z koszyka lokalnego - od tego momentu to
            // one w całości definiują zawartość koszyka konta.
            for (const item of items as any[]) {
                try {
                    await addToCart({
                        id: (item as any).id ?? item.product_id,
                        variation_id: item.variation_id,
                        quantity: getQuantityValue(item.quantity),
                    });
                } catch (itemError) {
                    console.error('❌ Nie udało się przenieść pozycji koszyka:', item, itemError);
                }
            }

            // UWAGA: świadomie NIE czyścimy tu starego koszyka gościa przez
            // POST /cart/clear?cart_key=<stary_klucz>. Gdy jesteśmy już
            // zalogowani (wysyłamy nonce), CoCart identyfikuje koszyk po
            // tożsamości z ciasteczka/nonce i IGNORUJE cart_key z URL — więc
            // taki request czyściłby koszyk KONTA (dopiero co uzupełniony!), a
            // nie starą wersję gościa. Stary, osierocony koszyk gościa zostanie
            // po prostu wygaszony przez standardowe czyszczenie sesji CoCart.
        } finally {
            clearCartKey();
            isMergingRef.current = false;
        }
    }, [queryClient]);

    // ------------------------------------------------------------
    // Sprawdzenie stanu logowania względem PRAWDZIWEJ sesji WP (ciasteczko).
    // Wykrywa też logowanie wykonane bezpośrednio w sklepie.
    // ------------------------------------------------------------
    const wasLoggedInRef = useRef(false);

    const refreshAuthState = useCallback(
        async (options?: { silent?: boolean }) => {
            const silent = options?.silent ?? false;

            // "silent" = sprawdzenie w tle (np. powrót do karty przeglądarki).
            // Celowo NIE ustawiamy wtedy isCheckingAuth — inaczej UI (np.
            // przyciski na stronie "Podsumowanie przed kasą") co chwilę znikałoby
            // na czas zapytania, mimo że dla użytkownika nic się nie zmieniło.
            // isCheckingAuth pokazujemy tylko przy pierwszym sprawdzeniu po
            // starcie aplikacji, gdzie faktycznie jeszcze nie wiemy nic.
            if (!silent) setIsCheckingAuth(true);
            try {
                const currentUser = await checkStatus();
                const wasLoggedIn = wasLoggedInRef.current;
                const nowLoggedIn = !!currentUser;

                setUser(currentUser);
                setIsLoggedIn(nowLoggedIn);
                wasLoggedInRef.current = nowLoggedIn;

                if (!wasLoggedIn && nowLoggedIn) {
                    // Przejście gość -> zalogowany, wykryte biernie (np. ktoś
                    // zalogował się bezpośrednio w sklepie). Scalamy koszyk
                    // dokładnie tak samo, jak przy jawnym logowaniu w React.
                    await mergeGuestCartIntoAccount();
                    queryClient.invalidateQueries({ queryKey: cartKeys.detail() });
                } else if (wasLoggedIn && !nowLoggedIn) {
                    // Wylogowano poza aplikacją — koszyk konta przestaje być
                    // aktualny, trzeba pobrać koszyk gościa na nowo.
                    queryClient.invalidateQueries({ queryKey: cartKeys.detail() });
                }
            } finally {
                if (!silent) setIsCheckingAuth(false);
            }
        },
        [queryClient, mergeGuestCartIntoAccount],
    );

    useEffect(() => {
        refreshAuthState();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Ponowna weryfikacja przy powrocie do karty — łapie przypadek "ktoś
    // zalogował się/wylogował w sklepie w międzyczasie, w innej karcie".
    // "silent: true" - nie chowamy UI na czas tego sprawdzenia.
    useEffect(() => {
        const handleFocus = () => {
            refreshAuthState({ silent: true });
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [refreshAuthState]);

    // ------------------------------------------------------------
    // Lokalne czyszczenie stanu (bez wywołania serwera) — używane gdy
    // ciasteczko/nonce zostały odrzucone przez CoCart w trakcie sesji.
    // To jest sytuacja awaryjna, więc tu celowo używamy removeQueries
    // (twardy reset) zamiast invalidateQueries.
    // ------------------------------------------------------------
    const clearLocalAuthState = useCallback(async () => {
        await queryClient.cancelQueries({ queryKey: cartKeys.detail() });

        clearNonce();
        clearCartKey();

        queryClient.removeQueries({ queryKey: cartKeys.detail() });

        setUser(null);
        setIsLoggedIn(false);
        wasLoggedInRef.current = false;

        await queryClient.refetchQueries({ queryKey: cartKeys.detail() });
    }, [queryClient]);

    useEffect(() => {
        const handleAuthInvalid = () => {
            console.warn('⚠️ Sesja została odrzucona przez serwer — wylogowuję lokalnie');
            setAuthError('Sesja wygasła — zaloguj się ponownie');
            clearLocalAuthState();
        };
        window.addEventListener('cocart:auth-invalid', handleAuthInvalid);
        return () => window.removeEventListener('cocart:auth-invalid', handleAuthInvalid);
    }, [clearLocalAuthState]);

    // ------------------------------------------------------------
    // Logowanie — ustawia PRAWDZIWE ciasteczko sesji WP (przez mu-plugin),
    // po czym jawnie scala koszyk gościa z kontem.
    // ------------------------------------------------------------
    const login = useCallback(
        async (username: string, password: string): Promise<boolean> => {
            setAuthError(null);
            const result = await loginRequest(username, password);

            if (!result.success || !result.user) {
                setAuthError(result.message || 'Nieprawidłowy login lub hasło');
                return false;
            }

            setUser(result.user);
            setIsLoggedIn(true);
            wasLoggedInRef.current = true;

            await mergeGuestCartIntoAccount();

            // invalidate (nie remove) — koszyk nadal jest widoczny na ekranie
            // podczas odświeżania w tle, zamiast na chwilę zniknąć.
            await queryClient.invalidateQueries({ queryKey: cartKeys.detail() });

            return true;
        },
        [queryClient, mergeGuestCartIntoAccount],
    );

    // ------------------------------------------------------------
    // Wylogowanie — kasuje PRAWDZIWE ciasteczko sesji WP po stronie serwera.
    // ------------------------------------------------------------
    const logout = useCallback(async () => {
        await queryClient.cancelQueries({ queryKey: cartKeys.detail() });

        await logoutRequest(); // kasuje ciasteczko sesji WP na serwerze

        clearCartKey();
        setUser(null);
        setIsLoggedIn(false);
        wasLoggedInRef.current = false;
        setAuthError(null);

        queryClient.removeQueries({ queryKey: cartKeys.detail() });
        await queryClient.refetchQueries({ queryKey: cartKeys.detail() });
    }, [queryClient]);

    return (
        <AuthContext.Provider
            value={{
                isLoggedIn,
                user,
                isCheckingAuth,
                authError,
                login,
                logout,
                refreshAuthState,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextValue => {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth musi być użyte wewnątrz <AuthProvider>');
    }
    return ctx;
};
